import { mat3 } from 'gl-matrix'

import type {
  IWebGlRender,
  MinMax,
  Nullable,
  Point3D,
  Property,
  ScaleType,
  VertexAttribute,
} from '../../../../types'
import {
  bindAttribute,
  bindIndexBuffer,
  createAttribute,
  getOrCreateProgram,
  getProjectionMatrix,
  getWebGlContext,
  unbindAttribute,
} from '../../../../utils'
import { updateBuffers } from './helpers'
import { fragmentShaderSource, vertexShaderSource } from './shaders'

type Attributes = 'aPosition' | 'aOffset'

export class LineRender implements IWebGlRender, Disposable {
  private canvas: HTMLCanvasElement
  private program: WebGLProgram
  private renderingContext: WebGL2RenderingContext

  private attributes: Record<Attributes, VertexAttribute>

  private indexBuffer: Nullable<WebGLBuffer>

  private globalProjectionUniform: Nullable<WebGLUniformLocation>
  private projectionUniform: Nullable<WebGLUniformLocation>
  private rangeMinUniform: Nullable<WebGLUniformLocation>
  private rangeMaxUniform: Nullable<WebGLUniformLocation>
  private paletteUniform: Nullable<WebGLUniformLocation>
  private resolutionUniform: Nullable<WebGLUniformLocation>

  private indexesCount: number

  private points: Point3D[][]

  private zoom: ScaleType
  private lineWidth: number
  private palette: number[]
  private range: MinMax

  private isDisposed: boolean

  constructor(
    canvas: HTMLCanvasElement,
    programs: Property<Record<string, WebGLProgram>>,
    zoom: ScaleType,
    lineWidth: number,
    points: Point3D[][],
    palette: number[],
  ) {
    this.canvas = canvas
    this.zoom = zoom
    this.points = points

    const gl = getWebGlContext(canvas)
    this.renderingContext = gl

    const program = getOrCreateProgram(programs, gl, vertexShaderSource, fragmentShaderSource)
    this.renderingContext = gl
    this.program = program

    this.attributes = {
      aPosition: createAttribute(gl, program, 'aPosition', 3, gl.FLOAT),
      aOffset: createAttribute(gl, program, 'aOffset', 3, gl.FLOAT),
    }

    this.indexBuffer = null
    this.globalProjectionUniform = gl.getUniformLocation(program, 'uGlobalProjection')
    this.projectionUniform = gl.getUniformLocation(program, 'uProjection')
    this.rangeMinUniform = gl.getUniformLocation(program, 'uRangeMin')
    this.rangeMaxUniform = gl.getUniformLocation(program, 'uRangeMax')
    this.paletteUniform = gl.getUniformLocation(program, 'uPalette[0]')
    this.resolutionUniform = gl.getUniformLocation(program, 'uResolution')

    this.indexesCount = 0

    this.lineWidth = lineWidth
    this.setLineWidth(lineWidth)
    this.palette = palette

    const allZ = this.points.flatMap(line => line.map(point => point.z))
    this.range = allZ.reduce<[number, number]>(
      (range, zValue) => {
        range[0] = Math.max(zValue, range[0])
        range[1] = Math.min(zValue, range[1])
        return range
      },
      [allZ[0], allZ[0]],
    )

    this.updateAttributeBuffers()

    this.isDisposed = false
  }

  public render() {
    if (this.isDisposed) return

    const renderingContext = this.renderingContext

    renderingContext.enable(renderingContext.BLEND)
    renderingContext.blendFunc(renderingContext.ONE, renderingContext.ONE_MINUS_SRC_ALPHA)

    renderingContext.useProgram(this.program)

    Object.entries(this.attributes).forEach(attr => {
      bindAttribute(renderingContext, attr[1])
    })

    bindIndexBuffer(renderingContext, this.indexBuffer)

    renderingContext.uniformMatrix3fv(
      this.globalProjectionUniform,
      false,
      getProjectionMatrix(this.canvas, this.zoom),
    )
    renderingContext.uniformMatrix3fv(this.projectionUniform, false, mat3.create())
    const width = this.canvas.width
    const height = this.canvas.height
    renderingContext.uniform4f(
      this.resolutionUniform,
      width / height,
      width,
      height,
      this.lineWidth,
    )
    renderingContext.uniform1f(this.rangeMinUniform, this.range[0])
    renderingContext.uniform1f(this.rangeMaxUniform, this.range[1])
    renderingContext.uniform4fv(this.paletteUniform, this.palette)

    renderingContext.drawElements(
      renderingContext.TRIANGLES,
      this.indexesCount,
      renderingContext.UNSIGNED_INT,
      0,
    )

    Object.entries(this.attributes).forEach(attr => {
      unbindAttribute(renderingContext, attr[1])
    })
  }

  public setRange([rangeMin, rangeMax]: MinMax) {
    this.range = [rangeMin, rangeMax]
    return this
  }

  public setZoomState(zoom: ScaleType) {
    this.zoom = zoom
  }

  public setLineWidth(lineWidth: number) {
    this.lineWidth = lineWidth
  }

  public setPoints(points: Point3D[][]) {
    this.points = points
    this.updateAttributeBuffers()
  }

  private updateAttributeBuffers() {
    const { indexBuffer: indexBuffer, indexesCount } = updateBuffers(
      this.renderingContext,
      this.attributes['aPosition'],
      this.attributes['aOffset'],
      this.points,
    )
    this.indexBuffer = indexBuffer
    this.indexesCount = indexesCount
  }

  [Symbol.dispose](): void {
    const renderingContext = this.renderingContext
    Object.entries(this.attributes).forEach(attr => {
      renderingContext.deleteBuffer(attr[1].buffer)
    })
    renderingContext.deleteBuffer(this.indexBuffer)
    this.isDisposed = true
  }
}
